import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import RestaurantIcon from "@mui/icons-material/Restaurant";

const findGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(86,195,114,0.32); }
  70%  { box-shadow: 0 0 0 14px rgba(86,195,114,0); }
  100% { box-shadow: 0 0 0 0 rgba(86,195,114,0); }
`;

const findPulseDot = keyframes`
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.35; }
  40% { transform: scale(1); opacity: 1; }
`;

const findIconPulse = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  38% { transform: translateY(-4px) scale(1.08); }
  68% { transform: translateY(1px) scale(0.98); }
`;

const findMapPulse = keyframes`
  0%   { transform: scale(0.98); opacity: 0.18; }
  60%  { transform: scale(1.02); opacity: 0.28; }
  100% { transform: scale(0.98); opacity: 0.18; }
`;

const findRadarExpand = keyframes`
  0% {
    transform: translate(-50%, -50%) scale(0.04);
    opacity: 0.48;
  }
  75% {
    opacity: 0.2;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.18);
    opacity: 0;
  }
`;

const findRadarRotate = keyframes`
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
`;

const FIND_LOADING_VARIANTS = {
  eat: {
    accent: "#FF6B4A",
    Icon: RestaurantIcon,
    title: "Finding nearby food...",
    subtitle: "Searching local bites and preparing time capsules on your map.",
  },
  drink: {
    accent: "#00A7C7",
    Icon: LocalBarIcon,
    title: "Finding nearby drinks...",
    subtitle: "Searching coffee, bars, lounges, and drink spots around this map.",
  },
  fun: {
    accent: "#5B4BFF",
    Icon: AutoAwesomeRoundedIcon,
    title: "Finding fun nearby...",
    subtitle: "Searching activities, sights, experiences, and hidden gems nearby.",
  },
  default: {
    accent: "",
    title: "Finding places for you...",
    subtitle: "Searching nearby results and preparing them on your map.",
  },
};

export default React.memo(function FindLoadingOverlay({
  open,
  isAndroidChrome = false,
  isIOSSafari = false,
  prompt = "",
  locationLabel = "",
  variant = "",
}) {
  const useLightweightEffects = isAndroidChrome || isIOSSafari;
  const [showHeavyRadarLayers, setShowHeavyRadarLayers] = useState(!useLightweightEffects);
  const displayPrompt = String(prompt || "").trim();
  const displayLocationLabel = String(locationLabel || "").trim();

  useEffect(() => {
    if (!open) {
      setShowHeavyRadarLayers(!useLightweightEffects);
      return;
    }
    if (!useLightweightEffects) {
      setShowHeavyRadarLayers(true);
      return;
    }
    setShowHeavyRadarLayers(false);
  }, [open, useLightweightEffects]);

  if (!open) return null;

  const radarFieldSize = useLightweightEffects ? "min(160vmax, 1600px)" : "220vmax";
  const backdropBlur = useLightweightEffects ? "none" : "blur(8px)";
  const backdropContentBlur = useLightweightEffects ? "none" : "blur(5px)";
  const sweepBlur = useLightweightEffects ? "none" : "blur(1px)";
  const loadingVariant = FIND_LOADING_VARIANTS[variant] || FIND_LOADING_VARIANTS.default;
  const LoadingIcon = loadingVariant.Icon || AutoAwesomeRoundedIcon;

  return (
    <Box
      data-testid="find-loading-overlay"
      data-lightweight-effects={useLightweightEffects ? "true" : "false"}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 20,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <Box
        sx={(theme) => ({
          "--find-loading-accent": loadingVariant.accent || theme.colors.green,
          position: "absolute",
          inset: 0,
          background: useLightweightEffects
            ? theme.palette.mode === "dark"
              ? `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.74)}, ${alpha(theme.colors.blue, 0.64)})`
              : `linear-gradient(180deg, ${alpha(theme.palette.common.white, 0.78)}, ${alpha(theme.colors.blue, 0.28)})`
            : `radial-gradient(circle at 50% 50%, ${alpha(loadingVariant.accent || theme.colors.green, 0.08)}, ${alpha(theme.colors.blue, 0.12)} 52%, ${alpha(theme.palette.common.black, 0.14)} 100%)`,
          backdropFilter: backdropContentBlur,
          WebkitBackdropFilter: backdropContentBlur,
          animation: useLightweightEffects ? "none" : `${findMapPulse} 2.2s ease-in-out infinite`,
          willChange: useLightweightEffects ? "auto" : "opacity, transform",
          transform: useLightweightEffects ? "none" : "translateZ(0)",
        })}
      />

      {showHeavyRadarLayers &&
        [0, 1, 2].map((ring) => (
          <Box
            key={`find-loading-radar-ring-${ring}`}
            sx={(theme) => ({
              position: "absolute",
              left: "50%",
              top: "50%",
              width: radarFieldSize,
              height: radarFieldSize,
              borderRadius: "50%",
              border: `1.4px solid ${alpha(loadingVariant.accent || theme.colors.green, useLightweightEffects ? 0.24 : 0.34)}`,
              boxShadow: useLightweightEffects
                ? `0 0 18px ${alpha(loadingVariant.accent || theme.colors.green, 0.14)}`
                : `0 0 28px ${alpha(loadingVariant.accent || theme.colors.green, 0.18)}`,
              transform: "translate(-50%, -50%) scale(0.04)",
              animation: `${findRadarExpand} 3.6s ease-out infinite`,
              animationDelay: `${ring * 1.2}s`,
              willChange: "transform, opacity",
              transformOrigin: "center",
            })}
          />
        ))}

      {showHeavyRadarLayers ? (
        <Box
          sx={(theme) => ({
            position: "absolute",
            left: "50%",
            top: "50%",
            width: radarFieldSize,
            height: radarFieldSize,
            borderRadius: "50%",
            background: `conic-gradient(from 0deg, transparent 0deg, ${alpha(loadingVariant.accent || theme.colors.green, useLightweightEffects ? 0.18 : 0.26)} 34deg, transparent 74deg)`,
            transform: "translate(-50%, -50%)",
            animation: `${findRadarRotate} ${useLightweightEffects ? "5.2s" : "4.6s"} linear infinite`,
            filter: sweepBlur,
            opacity: useLightweightEffects ? 0.62 : 0.75,
            willChange: "transform, opacity",
          })}
        />
      ) : null}

      <Box
        sx={(theme) => ({
          position: "absolute",
          left: "50%",
          top: "50%",
          width: 68,
          height: 68,
          transform: "translate(-50%, -50%)",
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `2px solid ${alpha(loadingVariant.accent || theme.colors.green, 0.5)}`,
          backgroundColor: alpha(loadingVariant.accent || theme.colors.green, 0.1),
          animation: `${findGlow} 1.8s ease-out infinite`,
          boxShadow: `0 0 0 8px ${alpha(loadingVariant.accent || theme.colors.green, 0.1)}`,
        })}
      >
        <CircularProgress
          size={30}
          thickness={5}
          sx={{ color: loadingVariant.accent || "success.main" }}
        />
      </Box>

      <Box
        sx={(theme) => ({
          position: "absolute",
          left: { xs: 16, md: "50%" },
          right: { xs: 16, md: "auto" },
          transform: { xs: "none", md: "translateX(-50%)" },
          width: {
            xs: "auto",
            md: "min(680px, calc(100vw - 48px))",
          },
          top: { xs: "14px", md: "18px" },
          borderRadius: "12px",
          px: { xs: 2.25, sm: 3 },
          py: { xs: 2.35, sm: 2.75 },
          minHeight: 184,
          maxHeight: { xs: "calc(var(--vh, 1vh) * 100 - 28px)", md: "calc(var(--vh, 1vh) * 100 - 36px)" },
          pointerEvents: "auto",
          backgroundColor: useLightweightEffects
            ? theme.palette.mode === "dark"
              ? "#17142b"
              : "#ffffff"
            : theme.palette.mode === "dark"
              ? alpha(theme.palette.background.paper, 0.98)
              : alpha(theme.palette.background.paper, 0.99),
          background: useLightweightEffects
            ? theme.palette.mode === "dark"
              ? `linear-gradient(180deg, #1c1835 0%, #151326 100%)`
              : `linear-gradient(180deg, #ffffff 0%, #f7f8ff 100%)`
            : theme.palette.mode === "dark"
              ? `radial-gradient(120% 140% at 0% 0%, ${alpha(loadingVariant.accent || theme.colors.green, 0.28)} 0%, ${alpha(theme.colors.blue, 0.24)} 42%, ${alpha(theme.palette.background.paper, 0.995)} 100%)`
              : `radial-gradient(120% 140% at 0% 0%, ${alpha(loadingVariant.accent || theme.colors.green, 0.22)} 0%, ${alpha(theme.colors.blue, 0.16)} 42%, ${alpha(theme.palette.background.paper, 0.998)} 100%)`,
          border: `2px solid ${alpha(loadingVariant.accent || theme.colors.green, 0.54)}`,
          boxShadow: useLightweightEffects
            ? `0 18px 44px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.62 : 0.24)}`
            : `0 18px 36px ${alpha(theme.palette.common.black, 0.34)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.26)}`,
          backdropFilter: backdropBlur,
          WebkitBackdropFilter: backdropBlur,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          justifyContent: "center",
          gap: 0.95,
          overflowY: "auto",
          overflowX: "hidden",
        })}
      >
        <Box
          sx={(theme) => ({
            width: 54,
            height: 54,
            borderRadius: "999px",
            display: "grid",
            placeItems: "center",
            color: theme.palette.common.white,
            backgroundColor: loadingVariant.accent || theme.colors.green,
            border: `3px solid ${alpha(theme.palette.common.white, 0.76)}`,
            boxShadow: `0 14px 28px ${alpha(loadingVariant.accent || theme.colors.green, 0.26)}`,
            animation: `${findIconPulse} 1.35s cubic-bezier(0.2, 0.85, 0.3, 1.2) infinite`,
          })}
        >
          <LoadingIcon sx={{ fontSize: 30 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 850, lineHeight: 1.12 }}>
          {loadingVariant.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            minHeight: 36,
            maxHeight: 36,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            textOverflow: "ellipsis",
            maxWidth: 420,
          }}
        >
          {loadingVariant.subtitle}
        </Typography>
        {displayPrompt ? (
          <Box
            sx={(theme) => ({
              width: "100%",
              maxWidth: 460,
              mt: 0.2,
              px: 1.35,
              py: 1,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.text.primary, 0.06),
              border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
            })}
          >
            <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}>
              Looking for
            </Typography>
            <Typography
              data-testid="find-loading-prompt-text"
              sx={{
                fontWeight: 900,
                lineHeight: 1.18,
                overflow: "visible",
                display: "block",
                WebkitLineClamp: "unset",
                WebkitBoxOrient: "unset",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              }}
            >
              {displayPrompt}
            </Typography>
          </Box>
        ) : null}
        {displayLocationLabel ? (
          <Box
            sx={(theme) => ({
              width: "100%",
              maxWidth: 460,
              px: 1.35,
              py: 0.85,
              borderRadius: 1,
              backgroundColor: alpha(loadingVariant.accent || theme.colors.green, 0.12),
              border: `1px solid ${alpha(loadingVariant.accent || theme.colors.green, 0.22)}`,
            })}
          >
            <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}>
              Searching in
            </Typography>
            <Typography sx={{ fontWeight: 850, lineHeight: 1.18 }}>
              {displayLocationLabel}
            </Typography>
          </Box>
        ) : null}
        <Box sx={{ display: "flex", gap: 0.8, mt: 0.25 }}>
          {[0, 1, 2].map((dot) => (
            <Box
              key={dot}
              sx={(theme) => ({
                width: 8,
                height: 8,
                borderRadius: "999px",
                backgroundColor: alpha(loadingVariant.accent || theme.colors.green, 0.92),
                animation: `${findPulseDot} 1.2s ease-in-out infinite`,
                animationDelay: `${dot * 0.18}s`,
              })}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
});
